import React, { useCallback, useEffect, useRef, useState } from 'react'
import { X, Search, Loader2, UserRound } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import {
  fetchPatronMatches,
  looksLikeMembershipNumber,
  patronMembershipNumber,
  patronSearchKey,
  patronSearchLabel,
  PATRON_SEARCH_DEBOUNCE_MS,
  PATRON_SEARCH_MIN_CHARS,
} from '../../../utils/patronProfileSearch'

const MIN_SEARCH_CHARS = PATRON_SEARCH_MIN_CHARS
const SEARCH_DEBOUNCE_MS = PATRON_SEARCH_DEBOUNCE_MS

function patronLabel(p) {
  return patronSearchLabel(p)
}

function patronKey(p) {
  return patronSearchKey(p)
}

function membershipDisplay(p) {
  return patronMembershipNumber(p)
}

/**
 * Link or replace the investigation case subject with a patron profile.
 * Staff search/display uses membership number; player id (MID) is used internally when linking.
 */
export default function ChangeLinkedProfileModal({
  open,
  onClose,
  caseId,
  currentMemberId,
  currentMembershipNumber = '',
  currentMemberName,
  onLinked,
}) {
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState([])
  const [searchError, setSearchError] = useState('')
  const [selected, setSelected] = useState(null)
  const [manualMembershipNumber, setManualMembershipNumber] = useState('')
  const [manualMemberName, setManualMemberName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const debounceRef = useRef(null)
  const searchSeqRef = useRef(0)

  const isChange = Boolean(
    (currentMembershipNumber && String(currentMembershipNumber).trim()) ||
    (currentMemberId && String(currentMemberId).trim())
  )
  const currentDisplayMembership =
    String(currentMembershipNumber || '').trim() ||
    String(currentMemberId || '').trim() ||
    ''

  useEffect(() => {
    if (!open) return undefined
    setQuery('')
    setResults([])
    setSelected(null)
    setManualMembershipNumber('')
    setManualMemberName('')
    setError('')
    setSearchError('')
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [open])

  const runSearch = useCallback(async (raw) => {
    const q = String(raw || '').trim()
    if (q.length < MIN_SEARCH_CHARS) {
      setResults([])
      setSearchError('')
      setSearching(false)
      return
    }
    const seq = ++searchSeqRef.current
    setSearching(true)
    setSearchError('')
    try {
      const rows = await fetchPatronMatches(q)
      if (seq !== searchSeqRef.current) return
      setResults(rows)
      if (!rows.length) {
        setSearchError(
          looksLikeMembershipNumber(q)
            ? 'No profile with that membership number'
            : 'No matching profiles found'
        )
      }
    } catch (e) {
      if (seq !== searchSeqRef.current) return
      setResults([])
      setSearchError(e.message || 'Search failed')
    } finally {
      if (seq === searchSeqRef.current) setSearching(false)
    }
  }, [])

  const onQueryChange = (value) => {
    setQuery(value)
    setSelected(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => runSearch(value), SEARCH_DEBOUNCE_MS)
  }

  const pick = (row) => {
    setSelected(row)
    setManualMembershipNumber(membershipDisplay(row) || '')
    setManualMemberName(patronLabel(row))
    setError('')
  }

  const resolveTarget = () => {
    const membershipNumber = String(
      manualMembershipNumber || membershipDisplay(selected) || ''
    ).trim()
    if (!membershipNumber && !selected?.MID) return null
    // Prefer club membership number as case member_id (staff-facing); keep player id when known
    const playerId = String(selected?.MID || '').trim() || null
    return {
      member_id: membershipNumber || playerId,
      membership_number: membershipNumber || null,
      player_id: playerId,
      member_name: String(manualMemberName || patronLabel(selected) || '').trim() || membershipNumber || playerId,
    }
  }

  const submit = async () => {
    const target = resolveTarget()
    if (!target) {
      setError('Select a profile or enter a membership number')
      return
    }
    const sameAsCurrent =
      (currentDisplayMembership && String(target.membership_number || target.member_id) === currentDisplayMembership) ||
      (currentMemberId && String(target.member_id) === String(currentMemberId).trim())
    if (isChange && sameAsCurrent) {
      setError('That profile is already linked to this case')
      return
    }
    const showMn = target.membership_number || target.member_id
    const confirmMsg = isChange
      ? `Replace linked profile with ${target.member_name} (Membership number ${showMn})?`
      : `Link ${target.member_name} (Membership number ${showMn}) as the case subject?`
    if (!window.confirm(confirmMsg)) return

    setBusy(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/confirm-suspect`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: target.member_id,
          member_name: target.member_name,
          membership_number: target.membership_number,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to link profile')
      onLinked?.(json.data || target)
      onClose?.()
    } catch (e) {
      setError(e.message || 'Failed to link profile')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {isChange ? 'Change linked profile' : 'Link profile'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {isChange
                ? 'Search for the correct patron and replace the subject on this investigation.'
                : 'Search for a patron to set as the subject on this investigation.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {isChange ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Currently linked</div>
              <div className="mt-0.5 font-medium">
                {currentMemberName || 'Unknown'}
                {currentDisplayMembership ? (
                  <span className="ml-2 font-mono text-xs text-slate-500">
                    Membership number {currentDisplayMembership}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Search name or membership number</span>
            <div className="relative mt-1">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="e.g. Eliana Day or 20027"
                autoFocus
              />
              {searching ? (
                <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
              ) : null}
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
              Number search matches membership number only (not player id).
            </p>
          </label>

          {searchError ? <p className="text-xs text-amber-700">{searchError}</p> : null}

          {results.length > 0 ? (
            <div className="max-h-48 overflow-auto rounded-lg border border-slate-200">
              {results.map((row) => {
                const key = patronKey(row)
                const active = selected && patronKey(selected) === key
                const mn = membershipDisplay(row)
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => pick(row)}
                    className={`flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2.5 text-left text-sm last:border-0 ${
                      active ? 'bg-teal-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <UserRound size={14} className="shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-slate-900">{patronLabel(row)}</span>
                      <span className="block truncate font-mono text-xs text-slate-500">
                        {mn ? `Membership number ${mn}` : 'No membership number'}
                        {row.MasterProfileID ? ` · ${row.MasterProfileID}` : ''}
                      </span>
                    </span>
                    {active ? <span className="text-[10px] font-bold uppercase text-teal-700">Selected</span> : null}
                  </button>
                )
              })}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-xs font-semibold text-slate-600">
              Membership number
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                value={manualMembershipNumber}
                onChange={(e) => {
                  setManualMembershipNumber(e.target.value)
                  setSelected(null)
                }}
                placeholder="Required"
              />
            </label>
            <label className="block text-xs font-semibold text-slate-600">
              Display name
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={manualMemberName}
                onChange={(e) => setManualMemberName(e.target.value)}
                placeholder="Optional"
              />
            </label>
          </div>

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !String(manualMembershipNumber || selected?.MembershipNumber || '').trim()}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: '#0a5169' }}
          >
            {busy ? 'Saving…' : isChange ? 'Change profile' : 'Link profile'}
          </button>
        </div>
      </div>
    </div>
  )
}
